import { useEffect, useId, useMemo } from "react"

import { Bar } from "react-chartjs-2"
import { useSelector } from "react-redux"

import EmptyComponent from "../../../Components/Empty"
import { useIncognito } from "../../../hooks/useIncognito"
import usePrefersReducedMotion from "../../../hooks/usePrefersReducedMotion"
import { tooltipLine } from "../../DashboardPage/components/chartsPlagins"
import { formatCount } from "../utils/formats"
import { yAxisRange } from "./axis"
import { intervalChartOptions, tooltipDateLabel } from "./chartBase"
import { externalTooltip } from "./chartTooltip"

import styles from "./charts.module.less"

// The page's stacked bar chart (DAA-2-25): segments edge to edge, a gap between the columns, the
// platform's dashed guide on hover, and a legend plus a "Total" tooltip row when there is more than
// one series, or always with `legend`. All-zero series are left out; bars grow in from their base
// (DAA-1-20) unless the system asks for reduced motion. `series` is [{ key, label, color, data }];
// `yWhole` steps the axis in whole units (counts, whole dollars).
const StackedBarChart = ({
  labels,
  series,
  yFormat = formatCount,
  yWhole = yFormat === formatCount,
  totalLabel = "Total",
  legend,
  height = 260,
  tooltipId
}) => {
  const theme = useSelector((state) => state.nav.theme)
  const incognito = useIncognito()
  const prefersReducedMotion = usePrefersReducedMotion()
  const generatedId = useId()
  const elementId = tooltipId ?? `admin-deflect-tooltip-${generatedId}`

  useEffect(() => () => document.getElementById(elementId)?.remove(), [elementId])

  const bars = useMemo(
    () => series.filter((segment) => segment.data.some((value) => value)),
    [series]
  )
  const showLegend = legend ?? bars.length > 1

  const data = useMemo(
    () => ({
      labels,
      datasets: bars.map((segment) => ({
        label: segment.label,
        data: segment.data,
        backgroundColor: segment.color,
        // A hovered column keeps its colours, as the platform's bars do.
        hoverBackgroundColor: segment.color,
        borderWidth: 0,
        categoryPercentage: 1,
        barPercentage: 0.78
      }))
    }),
    [labels, bars]
  )

  const options = useMemo(() => {
    const totals = labels.map((_label, dataIndex) =>
      bars.reduce((sum, segment) => sum + (segment.data[dataIndex] ?? 0), 0)
    )
    const rowsFor = (dataIndex, chart) => {
      const shown = bars.filter((_segment, index) => chart.isDatasetVisible(index))
      const total = shown.reduce((sum, segment) => sum + (segment.data[dataIndex] ?? 0), 0)

      return {
        header: tooltipDateLabel(labels[dataIndex]),
        rows: shown.map((segment) => ({
          marker: { color: segment.color },
          label: segment.label,
          value: yFormat(segment.data[dataIndex])
        })),
        total: showLegend ? { label: totalLabel, value: yFormat(total) } : null
      }
    }

    const chartOptions = intervalChartOptions({
      theme,
      labels,
      range: yAxisRange(totals, { whole: yWhole }),
      yFormat,
      legend: showLegend,
      tooltip: externalTooltip({
        tooltipId: elementId,
        rowsFor,
        offset: bars.length > 1 ? 16 : 20
      }),
      stacked: true
    })

    return {
      ...chartOptions,
      ...(prefersReducedMotion ? { animation: false } : {}),
      plugins: { ...chartOptions.plugins, "vertical-line-plugin": { theme } }
    }
  }, [
    theme,
    labels,
    bars,
    yFormat,
    yWhole,
    totalLabel,
    showLegend,
    elementId,
    prefersReducedMotion
  ])

  // As tall as the chart, so the card keeps its height when a filter empties it.
  return (
    <div className={styles.chart} style={{ height }}>
      {labels.length && bars.length ? (
        <Bar key={`${theme}-${incognito}`} data={data} options={options} plugins={[tooltipLine]} />
      ) : (
        <div className={styles.empty}>
          <EmptyComponent type="no_data_available" style={{ minHeight: height }} />
        </div>
      )}
    </div>
  )
}

export default StackedBarChart
